import React from 'react';
import { Images, PermissionList, RolePermission } from 'utils';
import {
    DateRow,
    DateRowStartEnd,
    LinkRow,
    ManageActions,
    PriceRow,
    renderCptCodeAndModifiers,
    SimpleTooltip,
    TextRow, TypeRow,
    UnitsRow,
} from 'components';
import { OpenClaimDetails } from '../claim/openClaimDetails';
import moment from 'moment/moment';
import { CLOSED_CLAIM, PAID } from '../../../fragments/billing/claims/claimDetails/constants';
import {submittedViaList} from "../claim/constants";

/**
 * Terminal states of a claim — the Completed tab's "Status" column filter.
 *
 * Exported rather than inlined in the head so the SelectedFilters chip in claims.js can label a
 * selection from the same list the column offered it from. The column prepends its own `All`
 * option; that is a control, not a value, so it stays out of here.
 */
export const completedClaimStatusList = [
    { name: 'Completed', id: PAID },
    { name: 'Closed', id: CLOSED_CLAIM },
];

const renderColorAndText = (status) => {
    switch (status) {
        case 'PAID':
            return {
                backgroundColor: '#ECFDF3',
                circleColor: '#027A48',
                color: '#027A48',
                text: 'Completed',
            };
        case 'CLOSED':
            return {
                backgroundColor: '#FEF3F2',
                circleColor: '#B42318',
                color: '#B42318',
                text: 'Closed',
            };
        default:
            return { backgroundColor: '', circleColor: '', color: '', text: '' };
    }
};

export const completedClaimTableInfo = (navigate, info, afterSuccess) => {
    return {
        loadingType: 'GET_CLAIMS',
        head: [
            { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'Claim ID', width: '7%' },
            { name: 'dateRange', title: 'DOS', iconType: 'date', disabled: true, width: '7%' },
            { name: 'subDate', title: 'Sub. Date', disabled: true, width: '8%' },
            {
                name: 'submittedVia',
                title: 'Source',
                disabled: true,
                width: '8%',
                filterList: [{ name: 'All', id: 'All' }, ...submittedViaList],
            },
            { name: 'funder', rowName: 'name', title: 'Payer', disabled: true, width: '10%' },
            { name: 'client', rowName: 'code', title: 'Client Code', disabled: true, width: '9%' },
            { name: 'billedAmt', title: 'Billed AMT', disabled: true, iconType: 'date', width: '9%' },
            { name: 'allowedAmt', title: 'Allowed AMT', disabled: true, iconType: 'date', width: '9%' },
            { name: 'paidAmount', title: 'Paid AMT', disabled: true, iconType: 'date', width: '9%' },
            { name: '', title: 'Balance', custom: false, width: '9%' },
            // { name: '', title: 'Paid On/By', custom: false, width: '10%' },
            {
                name: 'status',
                title: 'Status',
                disabled: true,
                width: '14%',
                filterList: [{ name: 'All', id: 'All' }, ...completedClaimStatusList],
            },
        ],

        body: (item) => {
            const { backgroundColor, circleColor, color, text } = renderColorAndText(item?.status);

            return [
                {
                    value: (
                        <div className="text-style flex-align-center" style={{ gap: 8 }}>
                            <OpenClaimDetails claimId={item?.id} displayId={item?.displayId} afterSuccess={afterSuccess} />
                            {/*<p style={activeStyles} onClick={(e) => handleOpen(e, item?.id)}>*/}
                            {/*    {item?.displayId}*/}
                            {/*</p>*/}
                        </div>
                    ),
                    width: '7%',
                    notClickable: true,
                },
                { value: <DateRowStartEnd start={item?.dos?.early} end={item?.dos?.latest} secondLine={true} />, width: '7%' },
                { value: <DateRow date={item?.submittedDate} />, width: '8%' },

                { value: <TypeRow text={item?.submittedVia ? item?.submittedVia : '--'} />, width: '8%' },


                {
                    value: (
                        <LinkRow
                            name={item?.funderName ? item.funderName : '--'}
                            href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.funderId}` : ''}
                            checkPermission={item?.funderId ? RolePermission([PermissionList.FS_READ.code]) : null}
                        />
                    ),
                    width: '10%',
                },
                {
                    value: (
                        <LinkRow
                            name={item?.clientFullName ? item?.clientFullName : item?.clientCode ? item?.clientCode : '--'}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                            checkPermission={item?.clientCode ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        />
                    ),
                    notClickable: true,
                    width: '9%',
                },
                { value: <PriceRow info={item?.billedAmount ? item?.billedAmount : 0} />, width: '9%' },
                { value: <PriceRow info={item?.allowedAmount ? item?.allowedAmount : 0} />, width: '9%' },

                {
                    value:
                        item?.paidAmount > 0 ? (
                            <ManageActions
                                wrapperStyles={{ width: '100%' }}
                                btnClass={'paid-amt-btn'}
                                btnTitle={<PriceRow info={item?.paidAmount} />}
                                renderBody={() =>
                                    !!item?.paymentLinks?.length && (
                                        <div className="column" style={{ alignItems: 'flex-start' }}>
                                            {item?.paymentLinks?.map((i, j) => (
                                                <a
                                                    key={j}
                                                    onClick={(e) => e?.stopPropagation()}
                                                    style={{ marginBottom: 8 }}
                                                    className="paid-amt-btn"
                                                    href={`/claim-payment/${i?.remittanceId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {i?.remittanceDisplayId}
                                                </a>
                                            ))}
                                        </div>
                                    )
                                }
                            />
                        ) : (
                            <PriceRow info={item?.paidAmount ? item?.paidAmount : 0} />
                        ),
                    width: '9%',
                },

                {
                    value: <PriceRow info={(item?.allowedAmount || 0) - (item?.clientTotal || 0) - (item?.paidAmount || 0)} />,
                    width: '9%',
                },

                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <button className="status-card-box" style={{ backgroundColor: backgroundColor, border: 'none', height: 28 }}>
                                {circleColor && <div style={{ backgroundColor: circleColor }} />}
                                <p style={{ color: color }}>{text ? text : '--'}</p>
                            </button>
                            {item?.completedBy && item?.completedAt && (
                                <div style={{ marginTop: 2 }}>
                                    <SimpleTooltip
                                        title={
                                            <div>
                                                <p>{`Marked by: ${item?.completedBy}`}</p>
                                                <p>{`Date: ${moment(item?.completedAt).format('MMMM D, YYYY')}`}</p>
                                            </div>
                                        }
                                        arrow
                                        placement="top">
                                        <img src={Images.toolInfoIcon} alt="" />
                                    </SimpleTooltip>
                                </div>
                            )}
                        </div>
                    ),
                    notClickable: true,
                    width: '14%',
                },
            ];
        },

        renderAccordionRow: (i) => i?.bills,

        accordionHead: [
            { title: 'ID', width: '13%' },
            { title: 'DOS', width: '13%' },
            { title: 'CPT Code', width: '14%' },
            { title: 'Units', width: '10%' },
            { title: 'Billed', width: '10%' },
            { title: 'Allowed', width: '10%' },
            { title: 'Client Total', width: '10%' },
            { title: 'Paid', width: '10%' },
            { title: 'Balance', width: '10%' },
        ],

        accordionBody: (i) => [
            { value: i?.displayId, width: '13%' },
            { value: <DateRow date={i?.dos} />, width: '13%' },
            {
                value: <TextRow name={renderCptCodeAndModifiers(i?.cptCode, i?.chargeRateModifier, i?.chargeRateModifier2, i?.chargeRateModifier3, i?.posModifier)} />,
                width: '14%',
            },
            { value: <UnitsRow info={i?.totalUnits} />, width: '10%' },
            { value: <PriceRow info={i?.totalBilled} />, width: '10%' },
            { value: <PriceRow info={i?.allowedAMT} />, width: '10%' },
            { value: <PriceRow info={i?.clientTotal} />, width: '10%' },
            { value: <PriceRow info={i?.payorPaid} />, width: '10%' },
            { value: <PriceRow info={i?.payorBalance} />, width: '10%' },
        ],

        noDetailsText: 'No bills yet',
        notYet: {
            title: 'No Completed Claims',
            subTitle: 'There are no completed claims.',
            image: Images.noPendingClaim,
        },
    };
};
